import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { Reveal } from "@/components/shared/Reveal";
import { ShareBar } from "@/components/shared/ShareBar";
import { INSIGHTS_META, INSIGHT_REDIRECTS } from "@/data/insightsMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InsightDetail() {
  const { slug } = useParams();
  const [a, setA] = useState(null);
  const [status, setStatus] = useState("loading");
  const redirectTo = INSIGHT_REDIRECTS[slug];
  const meta = a || INSIGHTS_META[slug];
  useSEO({
    title: meta ? `${meta.title} | Wexoz Insights` : "Insight | Wexoz",
    type: "article",
    description: meta?.excerpt,
    image: meta?.cover?.startsWith("/") ? meta.cover.replace(".webp", "-og.jpg") : meta?.cover,
    jsonLd: meta ? { "@context": "https://schema.org", "@type": "Article", headline: meta.title, description: meta.excerpt, datePublished: meta.published_at, author: { "@type": "Organization", name: "Wexoz Security & Surveillance LLC" }, publisher: { "@id": "https://wexoz.com/#organization" } } : undefined,
  });

  useEffect(() => {
    if (redirectTo) return;
    setStatus("loading");
    axios.get(`${API}/insights/${slug}`).then((r) => { setA(r.data); setStatus("ok"); }).catch(() => setStatus("error"));
  }, [slug, redirectTo]);

  if (redirectTo) return <Navigate to={`/insights/${redirectTo}`} replace />;

  if (status === "error") {
    if (!INSIGHTS_META[slug]) return <Navigate to="/insights" replace />;
    return (
      <section className="wrap min-h-[60vh] pt-40" data-testid="insight-not-found">
        <h1 className="text-4xl font-semibold">Article temporarily unavailable</h1>
        <Link to="/insights" className="btn-ghost mt-8">Back to Insights</Link>
      </section>
    );
  }
  if (!a) return <div className="wrap min-h-[60vh] pt-40" data-testid="insight-loading"><div className="h-10 w-2/3 animate-pulse rounded bg-navy-800" /></div>;

  return (
    <article data-testid="insight-article">
      <section className="relative isolate overflow-hidden pt-[76px]">
        <img src={a.cover} alt="" className="absolute inset-0 -z-20 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-navy/70 via-navy/85 to-navy" />
        <div className="wrap py-24 lg:py-32">
          <Link to="/insights" data-testid="insight-back-link" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white"><ArrowLeft className="h-4 w-4" /> Insights</Link>
          <div className="mt-8 flex items-center gap-3 font-mono text-[11px] uppercase tracking-wider text-slate-400">
            <span className="text-cyan-400">{a.category}</span><span>·</span><span>{a.read_time}</span><span>·</span><span>{a.published_at}</span>
          </div>
          <h1 className="mt-6 max-w-4xl text-4xl font-semibold leading-[1.05] sm:text-5xl lg:text-6xl">{a.title}</h1>
          <p className="mt-6 max-w-2xl text-lg text-slate-300">{a.excerpt}</p>
          <div className="mt-8"><ShareBar title={a.title} slug={a.slug} /></div>
        </div>
      </section>
      <section className="section pt-8">
        <div className="wrap grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-8 lg:col-start-2 space-y-6 text-base leading-relaxed text-slate-300 md:text-lg">
            {a.content.map((b, i) => {
              if (b.type === "h2") return <Reveal key={i}><h2 className="pt-6 text-2xl font-semibold text-white sm:text-3xl">{b.text}</h2></Reveal>;
              if (b.type === "quote") return <Reveal key={i}><blockquote className="my-4 border-l-2 border-cyan-400 bg-navy-800/50 px-6 py-5 font-heading text-xl font-medium leading-snug text-white sm:text-2xl">{b.text}</blockquote></Reveal>;
              if (b.type === "ul" || b.type === "ol") {
                const Tag = b.type;
                return (
                  <Reveal key={i}>
                    <Tag className={`space-y-3 pl-6 marker:text-cyan-400 ${b.type === "ol" ? "list-decimal marker:font-mono marker:text-sm" : "list-disc"}`}>
                      {b.items.map((it, j) => <li key={j} className="pl-1">{it}</li>)}
                    </Tag>
                  </Reveal>
                );
              }
              return <Reveal key={i}><p>{b.text}</p></Reveal>;
            })}
            <Reveal className="mt-12 flex flex-col gap-4 border-t border-line pt-8 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-slate-400">Found this useful? Share it with your team.</p>
              <ShareBar title={a.title} slug={a.slug} compact />
            </Reveal>
            <Reveal className="mt-8 card flex flex-col gap-4 p-8 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="eyebrow">Talk to Wexoz</div>
                <p className="mt-2 font-heading text-lg text-white">Have a related requirement? Let's design the right solution for it.</p>
              </div>
              <Link to="/contact" data-testid="insight-cta" className="btn-primary shrink-0">Talk to an Expert <ArrowUpRight className="h-4 w-4" /></Link>
            </Reveal>
          </div>
        </div>
      </section>
    </article>
  );
}
